import React, { memo, useState } from 'react';
import { Box, Button, ButtonBase, IconButton, Stack, Tooltip, Typography, alpha } from '@mui/material';
import { Add, EditOutlined, CallSplit } from '@mui/icons-material';

import PathwayDialog from './PathwayDialog';
import type { SchoolClass } from './sectionParts';
import { pathwayClassNames } from '../../../utils/admissionAssessment';
import type { AdmissionCycle, AssessmentPathway } from '../../../types/admissions';

interface Props {
  cycle: AdmissionCycle;
  classes: SchoolClass[];
  selected: AssessmentPathway;
  onSelect: (key: string) => void;
  canEdit: boolean;
  /** Save the cycle's pathways; `select` names the pathway to show next. */
  onSave: (pathways: AssessmentPathway[], select: string) => Promise<void>;
}

/**
 * Which group of classes the Selection process below is for. One pathway
 * reads as a single quiet line with "Split by class"; several show as cards
 * — the pathway's name over its classes — and the sections below edit the
 * selected one.
 */
const PathwaySwitcher = memo<Props>(({ cycle, classes, selected, onSelect, canEdit, onSave }) => {
  const [dialog, setDialog] = useState<{ pathway: AssessmentPathway | null } | null>(null);
  const pathways = cycle.pathways || [];

  const classesOf = (p: AssessmentPathway) => pathwayClassNames(cycle, p).join(', ') || 'No classes yet';

  const dialogEl = dialog && (
    <PathwayDialog
      open
      cycle={cycle}
      classes={classes}
      pathway={dialog.pathway}
      copyFrom={selected}
      onClose={() => setDialog(null)}
      onSave={onSave}
    />
  );

  if (pathways.length <= 1) {
    return (
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'center' }}
        sx={{ px: 2, py: 1.5, borderRadius: 2.5, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={600}>Every class follows one process</Typography>
          <Typography variant="body2" color="text.secondary">
            Assess some classes differently — e.g. an observation for the youngest, a test for the rest — by splitting them into their own pathway.
          </Typography>
        </Box>
        {canEdit && (
          <Button size="small" variant="outlined" startIcon={<CallSplit fontSize="small" />} onClick={() => setDialog({ pathway: null })} sx={{ textTransform: 'none', flexShrink: 0 }}>
            Split by class
          </Button>
        )}
        {dialogEl}
      </Stack>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(220px, 1fr))' }, gap: 1.25 }}>
        {pathways.map((p, i) => {
          const active = p.key === selected.key;
          return (
            <ButtonBase
              key={p.key}
              onClick={() => onSelect(p.key)}
              sx={(t) => ({
                display: 'block', textAlign: 'left', width: '100%', px: 2, py: 1.5, borderRadius: 2.5,
                border: 1.5,
                borderColor: active ? t.palette.primary.main : t.palette.divider,
                bgcolor: active ? alpha(t.palette.primary.main, 0.05) : t.palette.background.paper,
                transition: 'border-color 120ms, background-color 120ms',
                '&:hover': { borderColor: active ? t.palette.primary.main : alpha(t.palette.primary.main, 0.4) },
              })}
            >
              <Stack direction="row" alignItems="flex-start" spacing={1}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} color={active ? 'primary.main' : 'text.primary'} noWrap>{p.name}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {classesOf(p)}
                  </Typography>
                </Box>
                {canEdit && active && (
                  <Tooltip title={i === 0 ? 'Rename' : 'Rename or change classes'}>
                    <IconButton
                      size="small"
                      component="span"
                      onClick={(e) => { e.stopPropagation(); setDialog({ pathway: p }); }}
                      sx={{ mt: -0.5, mr: -0.75 }}
                    >
                      <EditOutlined sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </ButtonBase>
          );
        })}
        {canEdit && (
          <ButtonBase
            onClick={() => setDialog({ pathway: null })}
            sx={(t) => ({
              borderRadius: 2.5, border: 1.5, borderStyle: 'dashed', borderColor: t.palette.divider, minHeight: 60,
              color: 'text.secondary', gap: 0.75, '&:hover': { borderColor: t.palette.primary.main, color: 'primary.main' },
            })}
          >
            <Add fontSize="small" />
            <Typography variant="body2" fontWeight={600}>Add pathway</Typography>
          </ButtonBase>
        )}
      </Box>
      {dialogEl}
    </Box>
  );
});

PathwaySwitcher.displayName = 'PathwaySwitcher';

export default PathwaySwitcher;
